import type { EventCapturer } from '#src/analytics/analytics_event.ts';
import { GroupNameDrawEvent } from '#src/groups/analytics/group_analytics_events.ts';
import type { GroupClient } from '#src/groups/http/group_client.ts';
import type { RecordMatchViewRequest, RecordMatchViewResponse } from '@packages/contracts/group';
import { useMutation } from '@tanstack/react-query';

const GENERIC_ERROR_MESSAGE = 'An unexpected error occurred. Please try again.';

/**
 * Records that the participant viewed their match before the draw animation plays.
 * The server keeps an existing viewed timestamp and never changes the assignment.
 */
export function useRecordMatchView(groupClient: GroupClient, captureEvent: EventCapturer) {
  const mutation = useMutation<RecordMatchViewResponse, Error, RecordMatchViewRequest>({
    mutationFn: request => groupClient.recordMatchView(request),
    // Failure is tracked here because the API error is known immediately.
    // Success is tracked in DrawSlotMachine once the animation ends, confirming
    // the user actually saw their match.
    onSettled: result => {
      if (result?.success) return;
      captureEvent(GroupNameDrawEvent, {
        isSuccess: false,
        failureReason: result?.code ?? 'unexpected_error',
      });
    },
  });

  const { data: result, error: httpError, ...rest } = mutation;
  const hasError = httpError != null || result?.success === false;

  return {
    ...rest,
    result,
    httpError,
    isRecorded: result?.success === true,
    displayErrorMessage: hasError ? GENERIC_ERROR_MESSAGE : undefined,
  };
}
